import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Divider,
  Card,
  CardContent,
  Fade,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

// ---- Brand Palette ----
const BRAND = { 
  background: "#f0f5ff", 
  primary: "#1E3A8A", 
  secondary: "#3B82F6", 
  accentGray: "#4B5563", 
  accentGreen: "#10B981",
  paper: "#FFFFFF",
  border: "#E5E7EB",
};

const gradientText = {
  background: "linear-gradient(90deg, #6f92e4, #3d5eb9)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

// Reusable card styles
const cardStyles = {
  borderRadius: 4,
  bgcolor: BRAND.paper,
  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
  },
};

// Icon container
const iconContainerStyles = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  mb: 2,
  color: BRAND.secondary,
};

// Floating icon animation
const floatingStyle = (top: string, left: string, size: number, opacity: number, rotate?: boolean) => ({
  position: "absolute",
  top,
  left,
  fontSize: size,
  opacity,
  animation: rotate 
    ? "floatRotate 20s linear infinite" 
    : "floatUpDown 6s ease-in-out infinite alternate",
});

// Add keyframes globally
const styleSheet = `
@keyframes floatUpDown {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
}
@keyframes floatRotate {
  0% { transform: rotate(0deg) translateY(0px); }
  50% { transform: rotate(180deg) translateY(-15px); }
  100% { transform: rotate(360deg) translateY(0px); }
}
`;

const About: React.FC = () => {
  return (
    <Box sx={{ bgcolor: BRAND.background, minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative" }}>
      {/* Inject keyframes */}
      <style>{styleSheet}</style>

      {/* Floating background icons */}
      <PersonIcon sx={floatingStyle("50px", "20px", 120, 0.08)} />
      <LightbulbIcon sx={floatingStyle("300px", "80%", 100, 0.08)} />
      <EmojiEventsIcon sx={floatingStyle("600px", "10%", 140, 0.06)} />
      <SchoolIcon sx={floatingStyle("80%", "70%", 130, 0.05)} />
      <AttachMoneyIcon sx={floatingStyle("20%", "90%", 90, 0.06, true)} />
      <BarChartIcon sx={floatingStyle("75%", "15%", 110, 0.05, true)} />
      <TrendingUpIcon sx={floatingStyle("40%", "40%", 80, 0.07)} />
      <AccountBalanceIcon sx={floatingStyle("10%", "60%", 100, 0.06)} />

      {/* Header */}
      <Box component="section" sx={{ py: { xs: 8, sm: 10 }, bgcolor: BRAND.paper, borderBottom: `1px solid ${BRAND.border}`, position: "relative", zIndex: 1 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", mx: "auto" }}>
            <Fade in={true} timeout={500}>
              <Typography variant="h3" fontWeight={900} gutterBottom sx={{
  ...gradientText,
}}>
                About the Author & Project
              </Typography>
            </Fade>
            <Fade in={true} timeout={700}>
              <Typography variant="h6" sx={{ lineHeight: 1.6, color: BRAND.accentGray, fontWeight: 300, maxWidth: "80%", mx: "auto" }}>
                Learn more about the creator and the vision behind AthEcoScope.
              </Typography>
            </Fade>
            <Fade in={true} timeout={900}>
              <Divider sx={{ width: 100, mx: "auto", mt: 3, height: 4, bgcolor: BRAND.secondary, borderRadius: 2 }} />
            </Fade>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Box component="section" sx={{ py: { xs: 8, sm: 10 }, flexGrow: 1, position: "relative", zIndex: 1 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">

            {/* Author Card */}
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1200}>
                <Card elevation={0} sx={{ ...cardStyles, height: "100%", position: "relative", overflow: "hidden" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ ...iconContainerStyles, justifyContent: "center", gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 32 }} />
                      <Typography variant="h5" fontWeight={900} sx={{
  ...gradientText,
}}>
                        Author Bio
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, color: BRAND.accentGray, textAlign: "center" }}>
                      Hello everyone, I'm Athiban! The AthEcoScope is a website
                      built by me, a high school student and an aspiring economist
                      passionate about the intersection of data, finance, and
                      technology.
                      <br />
                      <br />
                      As just your fellow high-school student, I aim to use my skills to make challenging
                      ideas easier for anybody to understand. This project
                      reflects my desire to make complex data more accessible, and I
                      hope you find it both informative and engaging!
                    </Typography>
                    <PersonIcon sx={{ position: "absolute", top: -20, right: -20, opacity: 0.1, fontSize: 120, color: BRAND.secondary }} />
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            {/* Project Card */}
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1500}>
                <Card elevation={0} sx={{ ...cardStyles, height: "100%", position: "relative", overflow: "hidden" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ ...iconContainerStyles, justifyContent: "center", gap: 1 }}>
                      <LightbulbIcon sx={{ fontSize: 32 }} />
                      <Typography variant="h5" fontWeight={900} sx={{
  ...gradientText,
}}>
                        Project Background
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, color: BRAND.accentGray, textAlign: "center" }}>
                      AthEcoScope is part of a research project on India’s
                      evolving economy, with a special emphasis on UPI driving
                      digital payments. The platform seeks to make economic data
                      digestible and engaging through visuals and dashboards.
                      <br />
                      <br />
                      By combining in-depth analysis with intuitive design, the
                      AthEcoScope aims to help students, researchers, and
                      professionals understand the broader impacts of major
                      economic trends on society and markets.
                    </Typography>
                    <LightbulbIcon sx={{ position: "absolute", bottom: -10, left: -10, opacity: 0.08, fontSize: 140, color: BRAND.secondary }} />
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* Footer gradient strip */}
      <Box sx={{ height: 8, background: `linear-gradient(90deg, ${BRAND.primary}, ${BRAND.secondary})` }} />
    </Box>
  );
};

export default About;
